import { neverland as $, render, html, useState, useEffect } from "neverland";
import { isAuthenticated, onAuthChange, getCurrentUser, getCredentials, restoreSession, hasIdToken } from '../lib/cognito.js';
import database from '../lib/database.js';
import dataCache from '../lib/dataCache.js';
import { Prefetcher } from '../lib/prefetcher.js';
import { storeClientGroups } from '../lib/clientGroupCache.js';
import { getNormsDateLabel } from '../lib/normsLabel.js';
import { shouldFetchUserPermissions, shouldFetchSurveyData } from '../lib/authGating.js';
import { resolveLoginDecision } from '../lib/clusterAccess.js';

import utilities from "../lib/utilities.js";
import surveys from "../specs/menus.js";
import StyleSheet from "./StyleSheet.js";
import Router from "./Router.js";

let { assert } = utilities;

let prefetcher = null;

const App = $(function () {
	// Initialize auth state from localStorage if available
	const getInitialAuthState = () => {
		try {
			const savedAuth = localStorage.getItem('reportingAuth');
			if (savedAuth) {
				const parsed = JSON.parse(savedAuth);
				return {
					...parsed,
					db: database,
					themeDataLoaded: !!localStorage.getItem('reportingTheme'),
					userDataLoaded: !!(parsed.userData && parsed.groups),
					surveyDataLoaded: false,
					// Rehydrated from localStorage: the in-memory Cognito session is
					// gone, so authenticated fetches must wait until restoreSession()
					// rebuilds it on startup.
					sessionRestored: false
				};
			}
		} catch (err) {
			console.warn('Failed to load saved auth state:', err);
			localStorage.removeItem('reportingAuth');
		}
		return {
			isLoggedIn: false,
			currentUser: null,
			db: database,
			themeDataLoaded: !!localStorage.getItem('reportingTheme'),
			userDataLoaded: false,
			userData: {},
			groups: {},
			clientGroupId: null,
			clientGroupName: "",
			// Not logged in — nothing to restore; guests and the live-login path
			// proceed without blocking on restoreSession().
			sessionRestored: true
		};
	};

	const [auth, setAuth] = useState(getInitialAuthState());

	// When MFA is verified, complete the login
	useEffect(() => {
		if (auth.mfaVerified && auth.userData && !auth.isLoggedIn) {
			setAuth(prev => ({
				...prev,
				isLoggedIn: true,
				mfaVerified: false,
				mfaPage: null,
				cognitoUser: null,
				// Live-login path: auth.session already exists in memory, so no
				// restore is needed — unblock the authenticated data effects.
				sessionRestored: true
			}));
		}
	}, [auth.mfaVerified]);

	// Restore the Cognito session on a reload of a logged-in user. The session
	// (ID-token holder) is not persisted, so a rehydrated "logged in" state has
	// no session and authenticated data calls would fail with "invalid token".
	// Exchange the stored refresh token for a fresh session BEFORE any
	// authenticated fetch runs (those effects gate on auth.sessionRestored).
	useEffect(() => {
		if (auth.isLoggedIn && !auth.sessionRestored) {
			(async () => {
				const ok = await restoreSession();
				if (ok) {
					// Re-apply the shared login-completion gate against the
					// rehydrated auth (cognitoGroups/userEmail/userData — incl.
					// users[]/contacts[] — are persisted; only primaryPolls/polls
					// are stripped). A user whose membership became pending (or was
					// removed/rejected) between sessions must NOT be re-admitted to
					// the normal portal on reload. For a pending user, the Router
					// shows PendingApproval only when isLoggedIn:false &&
					// mfaPage:'PendingApproval', so divert there and skip the data
					// pipeline. Anything other than 'login' (register/revoked too,
					// e.g. a cold reload where userData.users is genuinely absent)
					// diverts rather than loading authenticated data.
					// restoreSession() has just set the Cognito session, so the
					// cluster-access lookup (run only when the school gate says
					// 'register') carries a token. It is recomputed here, never
					// trusted from storage, so a revoked cluster user is not
					// re-admitted; fresh login uses the same resolveLoginDecision.
					const gate = await resolveLoginDecision({
						email: auth.userEmail,
						groups: auth.cognitoGroups || [],
						userData: auth.userData,
						db: auth.db,
						isTokenReady: hasIdToken
					});
					if (gate.action !== 'login') {
						setAuth(prev => ({
							...prev,
							isLoggedIn: false,
							mfaPage: gate.action === 'pending' ? 'PendingApproval' : null,
							sessionRestored: true,
							userDataLoaded: false,
							surveyDataLoaded: false
						}));
						return;
					}
					// Flip the gate so the authenticated data effects below are
					// allowed to run, THEN drive the reload data pipeline directly
					// from the restored auth. On a reload, permissions are already
					// satisfied from cache (userDataLoaded:true) so the gated
					// fetchUserPermissions effect is a no-op; fetchSurveyData must
					// still run to repopulate primaryPolls/polls (stripped before
					// persisting). Kicking it here — in addition to the gated
					// effect — guarantees convergence regardless of effect re-run
					// timing after the async sessionRestored flip, so ReportHub
					// leaves its 'Loading...' phase. Build the restored auth
					// locally (same object the effect would see) to avoid relying
					// on a stale closure value.
					const restored = { ...auth, sessionRestored: true, clusterAccess: gate.clusterAccess };
					setAuth(prev => ({ ...prev, sessionRestored: true, clusterAccess: gate.clusterAccess }));
					if (shouldFetchUserPermissions(restored)) {
						// No cached groups — load permissions, which sets
						// userDataLoaded; the gated fetchSurveyData effect then runs.
						await fetchUserPermissions(restored);
					} else if (shouldFetchSurveyData(restored)) {
						await fetchSurveyData(restored);
					}
				} else {
					// Refresh token absent/expired/revoked — reset to logged-out
					// (router shows the login page) instead of looping on failing
					// authenticated requests. sessionRestored:true prevents re-entry.
					localStorage.removeItem('reportingAuth');
					localStorage.removeItem('reportingRefreshToken');
					setAuth(prev => ({
						...prev,
						isLoggedIn: false,
						userData: {},
						groups: {},
						userDataLoaded: false,
						surveyDataLoaded: false,
						sessionRestored: true
					}));
				}
			})();
		}
	}, []);

	// ---------------------------------------------------------------------------- //
	// ---------------------------------------------------------------------------- //
	// LOAD THEME ----------------------------------------------------------------- //
	// ---------------------------------------------------------------------------- //

	// Initialize theme from localStorage if available
	const getInitialTheme = () => {
		try {
			const savedTheme = localStorage.getItem('reportingTheme');
			if (savedTheme) {
				return JSON.parse(savedTheme);
			}
		} catch (err) {
			console.warn('Failed to load saved theme:', err);
			localStorage.removeItem('reportingTheme');
		}
		return {
			providerName: "",
			dashboardLinksTitle: "",
			dashboardLinks: [],
			dashboardText: "",
			userSupportEmail: "",
			userSupportPhone: "",
			userSupportName: "us",
			copyright: "",
		};
	};

	let [theme, setTheme] = useState(getInitialTheme());

	async function login() {
		if (!auth.isLoggedIn) {
			console.log("Authenticating with Cognito Identity Pool...");

			try {
				const credentials = await getCredentials();
				AWS.config.credentials = credentials;

				setAuth((prevAuth) => ({
					...prevAuth,
					isLoggedIn: true,
					currentUser: getCurrentUser()
				}));

				return true;
			} catch (err) {
				console.warn('Authentication failed:', err);
				return false;
			}
		}
		return true;
	}

	async function loadTheme() {
		// If theme is already loaded and cached, don't reload
		if (auth.themeDataLoaded && theme.surveys) {
			return;
		}

		// Check if theme is already cached in session
		const cachedTheme = localStorage.getItem('reportingTheme');
		
		if (cachedTheme) {
			try {
				const parsedTheme = JSON.parse(cachedTheme);
				parsedTheme.surveys = surveys(parsedTheme);
				setTheme(parsedTheme);
				setAuth(prevAuth => ({ ...prevAuth, themeDataLoaded: true }));
				return;
			} catch (err) {
				console.warn('Failed to parse cached theme:', err);
				localStorage.removeItem('reportingTheme');
			}
		}

		try {
			// Ensure credentials are available
			if (!AWS.config.credentials) {
				await getCredentials();
			}
			
			// Fetch theme data from database
			const themeData = await auth.db.collection('Themes').find({ "domains.hostname": window.location.hostname });
			const themeSpec = themeData.length > 0 ? themeData[0] : {};

			if (themeSpec && Object.keys(themeSpec).length > 0) {
				themeSpec.surveys = surveys(themeSpec);
				setTheme({...theme, ...themeSpec});
				
				// Cache the theme data in session
				try {
					localStorage.setItem('reportingTheme', JSON.stringify(themeSpec));
				} catch (cacheErr) {
					console.warn('Failed to cache theme:', cacheErr);
				}
			} else {
				console.warn('No theme found for hostname:', window.location.hostname);
			}
		} catch (err) {
			console.warn('Failed to load theme:', err);
		}

		setAuth(prevAuth => ({
			...prevAuth,
			themeDataLoaded: true
		}));

		// Pre-fetch norms date label (async, caches result for sync access in reports)
		getNormsDateLabel(database);
	}

	useEffect(() => {
		// Only initialize if theme is not already loaded
		if (!auth.themeDataLoaded) {
			// Always authenticate with Cognito Identity Pool for AWS credentials
			const initializeCredentials = async () => {
				try {
					const credentials = await getCredentials();
					AWS.config.credentials = credentials;
					loadTheme();
				} catch (err) {
					console.error('Failed to get AWS credentials:', err);
					// Still load theme even if credentials fail
					loadTheme();
				}
			};
			
			initializeCredentials();
		} else {
			// If theme is cached, still need to add surveys property
			if (theme && !theme.surveys) {
				setTheme(prevTheme => ({
					...prevTheme,
					surveys: surveys(prevTheme)
				}));
			}
		}
	}, []);

	useEffect(() => {
		if (theme !== null) {
			if (theme.hasOwnProperty("favicon")) {
				let fileType = theme.favicon.split(".")[1];

				document
					.getElementsByTagName("head")[0]
					.insertAdjacentHTML(
						"beforeend",
						`<link rel="icon shortcut" type="image/${fileType}" href="/static/img/favicons/${theme.favicon}" />`
					);
			}
		}
	}, [JSON.stringify(theme)]);

	// ---------------------------------------------------------------------------- //
	// ---------------------------------------------------------------------------- //
	// LOAD USER DATA ------------------------------------------------------------- //
	// ---------------------------------------------------------------------------- //

	async function fetchUserPermissions(auth) {
		try {
			// Get user data from the logged in client group
			let userData = auth.userData;

			// Extract permissions - this is all we need upfront
			let permissions = userData.permissions || [];
			let groups = {};
			for (let p of permissions) {
				groups[p] = true;
			}

			setAuth((prevAuth) => ({
				...prevAuth,
				userData,
				groups,
				clientGroupId: userData._id["$oid"],
				clientGroupName: userData.name,
				userDataLoaded: true
			}));

			return true;
		} catch (err) {
			console.error('Failed to fetch user permissions:', err);
			return false;
		}
	}

	async function fetchSurveyData(auth) {
		try {
			// Only load survey data if not already loaded
			if (auth.userData.primaryPolls) {
				return true;
			}

			const cacheKey = 'surveyData_' + auth.userData._id["$oid"];
			try {
				const cached = localStorage.getItem(cacheKey);
				if (cached) {
					const cachedData = JSON.parse(cached);
					if (cachedData.date === new Date().toLocaleDateString()) {
						auth.userData.primaryPolls = cachedData.primaryPolls;
						auth.userData.polls = cachedData.polls;
						setAuth((prevAuth) => ({
							...prevAuth,
							userData: auth.userData,
							surveyDataLoaded: true,
							surveyLoadProgress: null
						}));
						return true;
					}
				}
			} catch (e) { /* cache miss */ }

			let userData = auth.userData;
			const { db } = auth;

			setAuth(prev => ({ ...prev, surveyLoadProgress: { pc: 10, text: "Fetching surveys..." } }));

			// Only fetch fields needed for homepage and report filtering
			const surveyProjection = {
				name: 1, closeDate: 1, openDate: 1, accessCode: 1,
				dataClientGroup: 1, accessClientGroups: 1,
				archived: 1, type: 1, counts: 1, responseTotal: 1
			};

			let primaryPolls = await db.collection("Surveys").find({ dataClientGroup: userData._id["$oid"], archived: { $ne: true } }, { ...surveyProjection, _chunkSize: 500 });
			let polls = await db.collection("Surveys").find({ accessClientGroups: userData._id["$oid"], archived: { $ne: true } }, { ...surveyProjection, _chunkSize: 500 });

			setAuth(prev => ({ ...prev, surveyLoadProgress: { pc: 40, text: "Fetching school data..." } }));

			let clientGroupIds = [];
			for (let p of primaryPolls.concat(polls)) {
				clientGroupIds.push(p.dataClientGroup);
				if (p.accessClientGroups && Array.isArray(p.accessClientGroups)) {
					clientGroupIds = clientGroupIds.concat(p.accessClientGroups);
				}
			}

			let clientGroups = await db.collection("ClientGroups").find(
				{ _id: { $in: clientGroupIds.map(cg => ({"$oid": cg})) } },
				{ name: 1, suburb: 1, postcode: 1, _chunkSize: 500 }
			);

			// Populate the DataCache with ClientGroups data
			storeClientGroups(clientGroups);
			
			setAuth(prev => ({ ...prev, surveyLoadProgress: { pc: 60, text: "Processing survey data..." } }));

			primaryPolls = primaryPolls.map((p) => {
				p.dataClientGroup = clientGroups.find(c => c._id["$oid"] === p.dataClientGroup.toString());
				p.accessClientGroups = (p.accessClientGroups || []).map(cgId => 
					clientGroups.find(c => c._id["$oid"] === cgId.toString())
				).filter(Boolean);
				return p;
			});
			
			polls = polls.map((p) => {
				p.dataClientGroup = clientGroups.find(c => c._id["$oid"] === p.dataClientGroup.toString());
				p.accessClientGroups = (p.accessClientGroups || []).map(cgId => 
					clientGroups.find(c => c._id["$oid"] === cgId.toString())
				).filter(Boolean);
				return p;
			});

			// Response totals are precomputed server-side and stored on each Survey
			// as `responseTotal` (maintained by the write-time $inc in the Lambda
			// create path and reconciled hourly by the scheduled counts Lambda).
			// Reading the precomputed value replaces the previous per-load
			// count-many aggregation, which timed out (HTTP 504) for large clusters.
			const allSurveys = [...primaryPolls, ...polls];
			allSurveys.forEach(p => { p.total = p.responseTotal || 0; });

			userData.primaryPolls = primaryPolls;
			userData.polls = polls;

			try {
				localStorage.setItem(cacheKey, JSON.stringify({ date: new Date().toLocaleDateString(), primaryPolls, polls }));
			} catch (e) { /* storage full */ }

			setAuth((prevAuth) => ({
				...prevAuth,
				userData,
				surveyDataLoaded: true,
				surveyLoadProgress: null
			}));

			return true;
		} catch (err) {
			console.error('Failed to fetch survey data:', err);
			setAuth(prev => ({ ...prev, surveyLoadProgress: null }));
			return false;
		}
	}

	// Save auth state to localStorage whenever it changes
	useEffect(() => {
		if (auth.isLoggedIn && auth.userData) {
			try {
				const authToSave = {
					isLoggedIn: auth.isLoggedIn,
					currentUser: auth.currentUser,
					userEmail: auth.userEmail,
					cognitoGroups: auth.cognitoGroups,
					userData: {
						...auth.userData,
						primaryPolls: undefined,
						polls: undefined
					},
					groups: auth.groups,
					clientGroupId: auth.clientGroupId,
					clientGroupName: auth.clientGroupName,
					surveyDataLoaded: auth.surveyDataLoaded
				};
				localStorage.setItem('reportingAuth', JSON.stringify(authToSave));
			} catch (err) {
				console.warn('Failed to save auth state:', err);
			}
		} else if (!auth.isLoggedIn) {
			localStorage.clear();
			dataCache.clear();
			if (prefetcher) {
				prefetcher.stop();
				prefetcher = null;
			}
		}
	}, [auth.isLoggedIn, auth.userData, auth.groups, auth.clientGroupId, auth.clientGroupName, auth.surveyDataLoaded]);

	useEffect(() => {
		if (shouldFetchUserPermissions(auth)) {
			// Load permissions immediately (fast)
			if (!auth.groups || Object.keys(auth.groups).length === 0) {
				fetchUserPermissions(auth);
			} else {
				// If we have cached groups, just set userDataLoaded to true
				setAuth(prevAuth => ({ ...prevAuth, userDataLoaded: true }));
			}
		}
	}, [auth.isLoggedIn, auth.sessionRestored, auth.userData, auth.userDataLoaded]);

	// Background load survey data after user permissions are loaded
	useEffect(() => {
		if (shouldFetchSurveyData(auth)) {
			// Load survey data in background
			fetchSurveyData(auth);
		}
	}, [auth.isLoggedIn, auth.sessionRestored, auth.userDataLoaded, auth.surveyDataLoaded]);

	// Start prefetcher after survey metadata is loaded
	useEffect(() => {
		if (auth.isLoggedIn && auth.sessionRestored && auth.surveyDataLoaded && auth.userData?.primaryPolls) {
			if (!prefetcher) {
				prefetcher = new Prefetcher(dataCache, auth.db);
			}
			// Combine primaryPolls and polls for prefetch selection
			const allSurveys = [
				...(auth.userData.primaryPolls || []),
				...(auth.userData.polls || [])
			];
			// Items aren't loaded yet at this point, pass null — prefetcher handles it
			prefetcher.start(allSurveys, null);
		}
	}, [auth.isLoggedIn, auth.sessionRestored, auth.surveyDataLoaded]);

	// ---------------------------------------------------------------------------- //

	// ---------------------------------------------------------------------------- //

	return html`
		${StyleSheet(theme)}
		${auth.themeDataLoaded ? html`${Router(auth, setAuth, theme)}` : ""}
	`;
});

export default App;
